import { type JSXProps } from 'engine/index';
import { ArrayField, DropdownField, Form, NumberField, SubmitButton, TextField, validator as V } from './FormComponents';
import { lateinit, repeat, toTitleCase, unreachable } from 'shared/util';
import { FormGroup } from './FormGroup';
import { type Game, type GameData, GameSchema } from 'shared/modelTypes/index';
import { CheckCheck, MessageCircleQuestion, Palette, RectangleVertical } from 'lucide-static';
import { showToast, Toast } from './Toast';
import { navigate } from 'engine/router';
import { rpc } from 'src/backend';
import { Tab, TabView } from './TabView';
import { PersonalityColors, type PersonalityColor } from 'shared/modelTypes/team-kleuren';

type GameEditProps = {
	game: Game;
} & JSXProps;

export class GameEdit extends Base.Component<GameEditProps> {
	backLink: HTMLElement = lateinit;

	override render(): HTMLElement {
		const game = this.props.game;

		const view: HTMLElement = (
			<div class="flex flex-col gap-3">
				<h2 class="text-gray-700 dark:text-gray-300">Edit Game</h2>
				<div class="rounded-lg bg-white p-6 shadow-md dark:bg-gray-800">
					<Form schema={GameSchema} onSubmit={this.update.bind(this)} class="flex flex-col gap-6">
						<input type="hidden" name="_id" value={game._id} />
						<input type="hidden" name="kind" value={game.kind} />
						<div class="flex flex-col gap-6">
							<FormGroup>
								<TextField
									name="name"
									value={game.name}
									label="Name"
									validator={V(GameSchema._def.left._def.right.shape.name)}
									placeholder={game.kind}
								/>
							</FormGroup>
							{game.kind === 'team-kleuren-spel' && <TeamKleurenEditor data={game.data} />}
							{game.kind === 'kwaliteiten-jacht' && <KwaliteitenJachtEditor data={game.data} />}
						</div>
						<SubmitButton>Update</SubmitButton>
					</Form>
				</div>
			</div>
		);

		return view;
	}

	async update(game: Game) {
		this.view.style.pointerEvents = 'none';
		this.view.style.opacity = '0.6';
		const res = await rpc.updateGame.query(game);
		// console.log('got res', res);
		navigate('/dashboard/games');
		showToast(<Toast icon={CheckCheck} title="Game saved!" />, 3000);
	}
}

function TeamKleurenEditor({ data }: { data: GameData<'team-kleuren-spel'> }) {
	const cardCount = 48;
	const colorQuestionCount = 30;

	return (
		<FormGroup title="Game Data">
			<TabView class="overflow-hidden rounded-md border border-gray-200">
				<Tab name="cards" label="Kaarten" icon={RectangleVertical}>
					<h3 class="mb-3 font-bold text-gray-900">Kaarten</h3>
					<div class="flex flex-row gap-10">
						<ArrayField
							value={data.goodCards}
							label="Kaarten Goed"
							labelSingle="kaart"
							fixedSize={cardCount}
							entryBuilder={(v, i) => {
								const view = (
									<div class="flex gap-4">
										<TextField
											label="Name"
											compact
											labelWidth="5ch"
											value={v?.name}
											name={`data[goodCards][${i}][name]`}
										/>
										<DropdownField
											onChange={(v) => {
												// prettier-ignore
												switch (v) {
													case undefined:
														view.style
															.backgroundColor =
																"";
														break;
													case "blue":
														view.style
															.backgroundColor =
																"rgba(0,0,255,.1)";
														break;
													case "red":
														view.style
															.backgroundColor =
																"rgba(255,0,0,.1)";
														break;
													case "green":
														view.style
															.backgroundColor =
																"rgba(0,255,0,.1)";
														break;
													case "yellow":
														view.style
															.backgroundColor =
																"rgba(255,255,0,.1)";
														break;
													default:
														unreachable(v);
												}
											}}
											label="Color"
											options={PersonalityColors.map((c) => ({
												label: toTitleCase(c),
												value: c,
											}))}
											compact
											labelWidth="5ch"
											value={v?.color}
											name={`data[goodCards][${i}][color]`}
										/>
									</div>
								);
								return view;
							}}
						/>
						<ArrayField
							value={data.badCards}
							label="Kaarten Fout"
							labelSingle="kaart"
							fixedSize={cardCount}
							entryBuilder={(v, i) => {
								const view: HTMLElement = (
									<div class="flex gap-4">
										<TextField
											label="Name"
											compact
											labelWidth="5ch"
											value={v?.name}
											name={`data[badCards][${i}][name]`}
										/>
										<DropdownField
											onChange={(v) => {
												// prettier-ignore
												switch (v) {
													case undefined:
														view.style
															.backgroundColor =
																"";
														break;
													case "blue":
														view.style
															.backgroundColor =
																"rgba(0,0,255,.1)";
														break;
													case "red":
														view.style
															.backgroundColor =
																"rgba(255,0,0,.1)";
														break;
													case "green":
														view.style
															.backgroundColor =
																"rgba(0,255,0,.1)";
														break;
													case "yellow":
														view.style
															.backgroundColor =
																"rgba(255,255,0,.1)";
														break;
													default:
														unreachable(v);
												}
											}}
											label="Color"
											options={PersonalityColors.map((c) => ({
												label: toTitleCase(c),
												value: c,
											}))}
											compact
											labelWidth="5ch"
											value={v?.color}
											name={`data[badCards][${i}][color]`}
										/>
									</div>
								);
								return view;
							}}
						/>
					</div>
				</Tab>
				<Tab name="color-questions" label="Kleur vragen" icon={Palette}>
					<h3 class="mb-3 font-bold text-gray-900">Kleur vragen</h3>
					<ArrayField
						value={data.colorQuizQuestions}
						label="Kleur vragen"
						labelSingle="Kleur vraag"
						fixedSize={colorQuestionCount}
						entryBuilder={(v, i) => {
							const view: HTMLElement = (
								<div class="flex gap-4">
									<TextField
										label="Name"
										compact
										labelWidth="5ch"
										value={v?.question}
										size={60}
										name={`data[colorQuizQuestions][${i}][question]`}
									/>
									<DropdownField
										onChange={(v) => {
											// prettier-ignore
											switch (v) {
												case undefined:
													view.style.backgroundColor =
														"";
													break;
												case "blue":
													view.style.backgroundColor =
														"rgba(0,0,255,.1)";
													break;
												case "red":
													view.style.backgroundColor =
														"rgba(255,0,0,.1)";
													break;
												case "green":
													view.style.backgroundColor =
														"rgba(0,255,0,.1)";
													break;
												case "yellow":
													view.style.backgroundColor =
														"rgba(255,255,0,.1)";
													break;
												default:
													unreachable(v);
											}
										}}
										label="Color"
										options={PersonalityColors.map((c) => ({
											label: toTitleCase(c),
											value: c,
										}))}
										compact
										labelWidth="5ch"
										value={v?.color}
										name={`data[colorQuizQuestions][${i}][color]`}
									/>
								</div>
							);
							return view;
						}}
					/>
				</Tab>
				<Tab name="text-questions" label="Text vragen" icon={MessageCircleQuestion}>
					<h3 class="mb-3 font-bold text-gray-900">Text Questions</h3>
					<ArrayField
						value={data.textQuizzes}
						label="Text Questions"
						labelSingle="Text Question"
						entryBuilder={(v, i) => {
							const view = (
								<div class="mb-8 space-y-4">
									<div class="flex gap-4">
										<TextField
											label="Question"
											compact
											labelWidth="8ch"
											value={v?.question}
											size={60}
											name={`data[textQuizzes][${i}][question]`}
										/>

										{/* TODO: return numbers from dropdownfield */}
										{/* <DropdownField
									label="Correct"
									compact
									labelWidth="7ch"
									value={v?.correctAnswer}
									options={[
										{ label: 'A', value: 0 },
										{ label: 'B', value: 1 },
										{ label: 'C', value: 2 },
										{ label: 'D', value: 3 },
									]}
									name={`data[textQuizzes][${i}][correctAnswer]`}
								/> */}
										<NumberField
											label="Correct"
											name={`data[textQuizzes][${i}][correctAnswer]`}
											compact
											labelWidth="7ch"
											value={v?.correctAnswer ?? 0}
											min={0}
											max={3}
										/>
									</div>
									<div class="grid grid-cols-2 gap-4">
										{[0, 1, 2, 3].map((answerIndex) => (
											<TextField
												label={`Answer ${answerIndex + 1}`}
												compact
												labelWidth="9ch"
												value={v?.answers?.[answerIndex]}
												name={`data[textQuizzes][${i}][answers][${answerIndex}]`}
											/>
										))}
									</div>
								</div>
							);
							return view;
						}}
					/>
				</Tab>
			</TabView>
		</FormGroup>
	);
}

function KwaliteitenJachtEditor({ data }: { data: GameData<'kwaliteiten-jacht'> }) {
	const playerCount = 12;
	const cardCount = 7;

	return (
		<FormGroup>
			<h3 class="mb-3 font-bold text-gray-900">Start Kaarten</h3>

			<ArrayField
				horizontal
				value={data.startingCards}
				label="Speler Start kaarten"
				labelSingle="Speler"
				fixedSize={playerCount}
				entryBuilder={(v, i) => {
					if (!v) v = repeat(cardCount, { name: '', color: '' });
					const view = (
						<div class="mb-8 space-y-4">
							<h4 class="mb-3 font-bold text-gray-900">Speler {i + 1}</h4>

							<div class="flex flex-col gap-2">
								{[0, 1, 2, 3, 4, 5, 6].map((j) => {
									const view2: HTMLElement = (
										<div>
											<TextField
												label={`Kaart ${j + 1}`}
												compact
												labelWidth="6ch"
												value={v![j].name}
												name={`data[startingCards][${i}][${j}][name]`}
											/>
											<DropdownField
												onChange={(v: PersonalityColor | undefined) => {
													// prettier-ignore
													switch (v) {
												case undefined:
													view2.style.backgroundColor =
														"";
													break;
												case "blue":
													view2.style.backgroundColor =
														"rgba(0,0,255,.1)";
													break;
												case "red":
													view2.style.backgroundColor =
														"rgba(255,0,0,.1)";
													break;
												case "green":
													view2.style.backgroundColor =
														"rgba(0,255,0,.1)";
													break;
												case "yellow":
													view2.style.backgroundColor =
														"rgba(255,255,0,.1)";
													break;
												default:
													unreachable(v);
											}
												}}
												label="Color"
												options={PersonalityColors.map((c) => ({
													label: toTitleCase(c),
													value: c,
												}))}
												compact
												labelWidth="5ch"
												value={v![j].color as PersonalityColor}
												name={`data[startingCards][${i}][${j}][color]`}
											/>
										</div>
									);

									return view2;
								})}
							</div>
						</div>
					);
					return view;
				}}
			/>
		</FormGroup>
	);
}
